import React, { useState, useRef, useMemo, useEffect } from 'react';
import { DropdownProps } from 'semantic-ui-react';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { getESPList } from '../../../Services/EmployerSpotlightPresentationServices';
import { AxiosResponse } from 'axios';
import { utils } from '../../../helpers/utils';
import AppConfig from '../../../Constants/config';

export interface PageSizeOption {
    key: number;
    value: string | number;
    text: string;
}

export interface GetESPPayload {
    sortByField: string | null;
    sortOrderDesc: boolean;
    searchText: string | null;
    recordsPerPage: number;
    pageNum: number;
    isUpcoming: boolean;
    fromDate: string | null;
    toDate: string | null;

}

export interface GetESPDataRow {
    [key: string]: any;
    description: string | null;
    employerSpotlightPresentationId: number | null;
    organization: string | null;
    participantsCount: number;
    presentationDateTime: string| null
    startTime: string | null;
    topic: string | null;
}

export interface GetESPData {
    totalRecords: number;
    gridData: GetESPDataRow[];
}

export interface ESPListData {
    date: string;
    data: ESPListDataRow[];
};

export interface ESPListDataRow {
            employerSpotlightPresentationId: number | null;
            organization: string | null;
            topic: string | null;
            startTime: string | null;
            description: string | null;
}

export interface MasterDataItem {
    displayName: string;
    iconName?: string;
    id: number;
}

export interface MasterData {

}

export interface MasterDataResponse {

}

export function usePublicEmployerSpotlightPresentation(saveState: boolean = true, statePropName?: string) {
    const pageSizeOptions = useMemo<PageSizeOption[]>(() => {
        return [
            { key: 3, value: 3, text: '3' },
            { key: 5, value: 5, text: '5' },
            { key: 10, value: 10, text: '10' },
            { key: 15, value: 15, text: '15' },
            { key: 20, value: 20, text: '20' },
            { key: 50, value: 50, text: '50' },
            { key: 100, value: 100, text: '100' }
        ];
    }, []);

    const searchRef = useRef<HTMLInputElement>(null);
    const [masterData, setMasterData] = useState<MasterData | null>(null);
    const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [pageNum, setPageNum] = useState<number>(1);
    const [sortByField, setSortByField] = useState<string | null>(null);
    const [sortOrderDesc, setSortOrderDesc] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [data, setData] = useState<ESPListData[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isUpcoming, setIsUpcoming] = useState<boolean>(true);
    const [fromDate, setFromDate] = useState<string | null>(null);
    const [toDate, seToDate] = useState<string | null>(null);

    const handlePageSizeChange: ((event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => void) | undefined = (event, data) => {
        setRecordsPerPage(data.value as number);
        const payload: GetESPPayload = {
            recordsPerPage: data.value as number,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            isUpcoming: isUpcoming,
            fromDate: null,
            toDate: null
        };
        _fetchData(payload);
    }

    const handlePaginationChange: (newPageNumber: number) => void = (newPageNumber) => {
        setPageNum(newPageNumber);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: newPageNumber,
            isUpcoming: isUpcoming,
            fromDate: null,
            toDate: null
        };
        _fetchData(payload);
    }

    const handleSortChange: (newSortColumnName: string) => void = (newSortColumnName) => {
        let newIsDesc = sortOrderDesc;
        let newColumnName = sortByField;
        if (newSortColumnName === sortByField) {
            newIsDesc = !newIsDesc;
        } else {
            newColumnName = newSortColumnName;
        }

        setSortOrderDesc(newIsDesc);
        setSortByField(newColumnName);
        setPageNum(1);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: newColumnName,
            sortOrderDesc: newIsDesc,
            searchText: searchText,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: null,
            toDate: null
        };
        _fetchData(payload);
    }

    const handleSearchChangeFn: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        //setSearchText((event.target as HTMLInputElement).value);
        setSearchText((event.target as HTMLInputElement).value);
        setPageNum(1);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: (event.target as HTMLInputElement).value,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: null,
            toDate: null
        };
        _fetchData(payload);
    }
    const handleSearchChange = _.debounce(handleSearchChangeFn, 1000);

    const resetSearch: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        if (searchRef.current !== null) {
            (searchRef.current as HTMLInputElement).value = '';
        }
        setSearchText(null);
        setPageNum(1);
        const payload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            isUpcoming: isUpcoming,
            fromDate: null,
            toDate: null
        };
        _fetchData(payload);
    }

    const _fetchData: (overwritePayload?: GetESPPayload) => void = (overwritePayload) => {
        const defaultPayload: GetESPPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            isUpcoming: isUpcoming,
            fromDate: null,
            toDate: null
        };
        let payload: GetESPPayload = defaultPayload;
        if (overwritePayload !== undefined) {
            payload = overwritePayload;
        }
        //console.log(payload);
        setIsLoading(true);
        if (saveState) {
            _saveState(payload);
        }
        getESPList(payload, false).then((result) => {
            setIsLoading(false);
            //console.log(result);
            let response: AxiosResponse<unknown, GetESPData> = result as AxiosResponse;
            if (response.status === 200 && response.data) {
                let data = response.data as GetESPData;
                let reshapedData= _reshapeData(data);
                setData(reshapedData);
                setTotalRecords(data.totalRecords);
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchMasterData: (callback?: Function) => void = (callBack = () => { }) => {
        // there is no master data at the moment, but reserve this slot for possible future use.
        callBack();
    }

    const _reshapeData = (data: GetESPData): ESPListData[]=>{
        let dateList = new Map<string, number>();
        const timeFormat = 'M/D/yyyy'
        let result: ESPListData[] = [];
        if(data.totalRecords > 0){
            data.gridData.forEach((item)=>{
                let dateString = item.presentationDateTime?.split(' ')[0] || '';
                if(dateList.get(dateString) == null){
                    dateList.set(dateString, 1);
                }
            });
            dateList.forEach((value, key,)=>{
                let currDay:ESPListData = {
                    date: key,
                    data: [],
                };
                data.gridData.filter((item)=>{
                    return item.presentationDateTime?.startsWith(key);
                }).forEach((item)=>{
                    let temp: ESPListDataRow = {
                        employerSpotlightPresentationId: item.employerSpotlightPresentationId,
                        organization: item.organization,
                        topic: item.topic,
                        startTime: item.startTime,
                        description: item.description,
                    };
                    currDay.data.push(temp);
                });
                result.push(currDay);
            });
        }
        return result;
    }

    const _saveState = (currentPayload: GetESPPayload) => {
        let stateName = statePropName || AppConfig.PUBLIC_EMPLOYERSPOTLIGHTPRESENTION_LIST_STATE;
        if (saveState) {
            utils.updateInLocalStorage(stateName, JSON.stringify(currentPayload));
        }
    }

    const _loadState = () => {
        let stateName = statePropName || AppConfig.PUBLIC_EMPLOYERSPOTLIGHTPRESENTION_LIST_STATE;
        let stateString: string | null = utils.getLocalStorageItem(stateName);
        if (stateString && saveState) {
            return JSON.parse(stateString) as GetESPPayload;
        } else {
            return null;
        }

    }

    useEffect(() => {
        _fetchMasterData(() => {
            let priviousState: GetESPPayload | null = _loadState();
            if (priviousState && saveState) {
                _fetchData(priviousState);
                setSortByField(priviousState.sortByField);
                setSortOrderDesc(priviousState.sortOrderDesc);
                setSearchText(priviousState.searchText);
                setRecordsPerPage(priviousState.recordsPerPage);
                setPageNum(priviousState.pageNum);
                if (searchRef.current) {
                    searchRef.current.value = priviousState.searchText || '';
                }
            } else {
                _fetchData();
            }
        });

    }, []);

    return [recordsPerPage, totalRecords, pageNum,
        pageSizeOptions, searchRef, searchText, data, isLoading, sortOrderDesc, sortByField,
        handlePageSizeChange, handleSearchChange, resetSearch, handlePaginationChange, handleSortChange] as const;
}